import { useEffect, useState } from "react";
import { X, Loader2, Sparkles, Check, AlertCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const Row = ({ label, value, onChange, testid, max, textarea, help }) => (
  <div>
    <div className="flex items-center justify-between">
      <label className="label-caps">{label}</label>
      {max && (
        <span className={`text-[10px] font-medium ${(value || "").length > max ? "text-red-700" : "text-slate-400"}`}>
          {(value || "").length}/{max}
        </span>
      )}
    </div>
    {textarea ? (
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
                rows={textarea}
                className="mt-1.5 w-full rounded-md border border-slate-300 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
    ) : (
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="mt-1.5 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
    )}
    {help && <p className="mt-1 text-[11px] text-slate-500">{help}</p>}
  </div>
);

/**
 * SEO + AI-search review drawer. Pulls suggestions from backend, lets the author edit every
 * field, and writes them back onto the post via PUT /admin/blog/posts/:id.
 */
export function SeoReviewDrawer({ postId, open, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [post, setPost] = useState(null);
  const [draft, setDraft] = useState({});
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (!open || !postId) return;
    (async () => {
      try {
        const p = await api.get(`/admin/blog/posts/${postId}`, true);
        setPost(p);
        setDraft({
          meta_title: p.meta_title || p.title || "",
          meta_description: p.meta_description || p.excerpt || "",
          slug: p.slug,
          excerpt: p.excerpt || "",
          og_title: p.og_title || p.title || "",
          og_description: p.og_description || p.excerpt || "",
          twitter_title: p.twitter_title || p.title || "",
          twitter_description: p.twitter_description || p.excerpt || "",
          hero_alt: p.hero_alt || "",
          tldr: p.tldr || "",
          faq: p.faq || [],
          backlinks: p.backlinks || [],
          keywords: p.keywords || [],
          social_post: p.social_post || "",
        });
        setHealth(p.seo_health);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [open, postId]);

  if (!open) return null;

  const run = async () => {
    setRunning(true);
    try {
      const s = await api.post(`/admin/blog/posts/${postId}/seo-review`, {}, true);
      setDraft((d) => ({
        ...d,
        meta_title: s.meta_title || d.meta_title,
        meta_description: s.meta_description || d.meta_description,
        slug: s.slug || d.slug,
        excerpt: s.excerpt || d.excerpt,
        og_title: s.og_title || d.og_title,
        og_description: s.og_description || d.og_description,
        twitter_title: s.twitter_title || d.twitter_title,
        twitter_description: s.twitter_description || d.twitter_description,
        hero_alt: s.hero_alt || d.hero_alt,
        tldr: s.tldr || d.tldr,
        faq: s.faq?.length ? s.faq : d.faq,
        keywords: s.keywords?.length ? s.keywords : d.keywords,
        social_post: s.social_post || d.social_post,
        backlinks: s.suggested_backlinks?.length ? [...new Set([...(d.backlinks || []), ...s.suggested_backlinks])] : d.backlinks,
      }));
      setHealth(s.seo_health);
      toast.success("AI suggestions ready — review and save");
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/blog/posts/${postId}`, {
        ...draft,
        // keywords is already an array
      }, true);
      toast.success("SEO fields saved");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const addFaq = () => setDraft((d) => ({ ...d, faq: [...(d.faq || []), { q: "", a: "" }] }));
  const setFaq = (i, k, v) => setDraft((d) => ({
    ...d, faq: d.faq.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)),
  }));
  const rmFaq = (i) => setDraft((d) => ({ ...d, faq: d.faq.filter((_, idx) => idx !== i) }));

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} />
      <aside data-testid="seo-drawer"
             className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
              <Sparkles className="h-4 w-4 text-red-700" /> Optimise for SEO &amp; AI search
            </h3>
            <p className="text-xs text-slate-500">Every field is editable. Nothing applies until you save.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="seo-close" className="rounded-md p-1 text-slate-500 hover:bg-slate-200">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <button onClick={run} disabled={running} data-testid="seo-run"
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {running ? "Thinking..." : "Generate AI suggestions"}
            </button>
            <p className="text-xs text-slate-500">
              Fills every field from the current body. You can regenerate any time.
            </p>
          </div>

          {health && (
            <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="seo-health">
              <p className="label-caps">SEO health</p>
              <p className="mt-1 text-sm font-medium text-slate-800">Reading grade: {health.reading_grade || "—"}</p>
              {health.notes?.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-slate-600">
                  {health.notes.map((n, i) => (
                    <li key={i} className="flex gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />{n}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <Row label="Meta title" testid="seo-meta-title" max={60}
               value={draft.meta_title} onChange={(v) => setDraft({ ...draft, meta_title: v })} />
          <Row label="Meta description" testid="seo-meta-desc" max={155} textarea={2}
               value={draft.meta_description} onChange={(v) => setDraft({ ...draft, meta_description: v })} />
          <Row label="URL slug" testid="seo-slug"
               value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })}
               help={`Lives at ${post ? `/blog/${draft.slug}` : "/blog/..."}.`} />
          <Row label="Short excerpt" testid="seo-excerpt" max={200} textarea={2}
               value={draft.excerpt} onChange={(v) => setDraft({ ...draft, excerpt: v })} />
          <Row label="Hero image alt text" testid="seo-hero-alt"
               value={draft.hero_alt} onChange={(v) => setDraft({ ...draft, hero_alt: v })}
               help="Required before publishing." />
          <Row label="Open Graph title" testid="seo-og-title" max={70}
               value={draft.og_title} onChange={(v) => setDraft({ ...draft, og_title: v })} />
          <Row label="Open Graph description" testid="seo-og-desc" max={200} textarea={2}
               value={draft.og_description} onChange={(v) => setDraft({ ...draft, og_description: v })} />
          <Row label="Twitter card title" testid="seo-tw-title" max={70}
               value={draft.twitter_title} onChange={(v) => setDraft({ ...draft, twitter_title: v })} />
          <Row label="Twitter card description" testid="seo-tw-desc" max={200} textarea={2}
               value={draft.twitter_description} onChange={(v) => setDraft({ ...draft, twitter_description: v })} />
          <Row label="TL;DR for AI answer engines" testid="seo-tldr" textarea={4}
               value={draft.tldr} onChange={(v) => setDraft({ ...draft, tldr: v })}
               help="A standalone paragraph a model can quote verbatim." />

          <div>
            <label className="label-caps">Keywords / tags</label>
            <input data-testid="seo-keywords" value={(draft.keywords || []).join(", ")}
                   onChange={(e) => setDraft({ ...draft, keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean) })}
                   placeholder="peanut recall, allergen alert, fsanz"
                   className="mt-1.5 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" />
            <p className="mt-1 text-[11px] text-slate-500">Comma-separated. Also used as hashtags on article cards.</p>
          </div>

          <Row label="Social post (X / Twitter)" testid="seo-social" textarea={3} max={270}
               value={draft.social_post} onChange={(v) => setDraft({ ...draft, social_post: v })}
               help="Ready to paste when you share the article." />

          <div data-testid="seo-faq">
            <div className="flex items-center justify-between">
              <p className="label-caps">FAQ (adds FAQPage schema)</p>
              <button onClick={addFaq} data-testid="seo-faq-add" className="text-xs font-semibold text-red-700">+ Add Q&amp;A</button>
            </div>
            <ul className="mt-2 space-y-3">
              {(draft.faq || []).map((f, i) => (
                <li key={i} className="rounded-md border border-slate-200 p-3">
                  <input value={f.q} data-testid={`seo-faq-q-${i}`} onChange={(e) => setFaq(i, "q", e.target.value)}
                         placeholder="Question"
                         className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
                  <textarea value={f.a} data-testid={`seo-faq-a-${i}`} onChange={(e) => setFaq(i, "a", e.target.value)}
                            placeholder="Answer" rows={2}
                            className="mt-2 w-full rounded-md border border-slate-200 p-2 text-sm" />
                  <button onClick={() => rmFaq(i)} data-testid={`seo-faq-rm-${i}`} className="mt-1 text-xs text-red-700">Remove</button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button onClick={save} disabled={busy} data-testid="seo-save"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-red-700 px-4 py-2.5 font-semibold text-white hover:bg-red-800 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save SEO changes
          </button>
        </footer>
      </aside>
    </>
  );
}
